import { cn } from '@ophelos/ui/lib/utils';
import { Bug, Flag, Search, Zap } from 'lucide-react';
import { EmptyMedia } from '@/components/ui/empty';
import type { AgentConfig, StarterPrompt, StarterPromptIcon } from '../agent-registry';

const PROMPT_ICONS = {
  search: Search,
  bug: Bug,
  zap: Zap,
  flag: Flag,
} satisfies Record<StarterPromptIcon, typeof Search>;

interface WelcomeScreenProps {
  agentConfig: AgentConfig;
  onChoosePrompt: (prompt: StarterPrompt) => void;
  disabled?: boolean;
}

export function WelcomeScreen({ agentConfig, onChoosePrompt, disabled }: WelcomeScreenProps) {
  const Icon = agentConfig.icon;

  return (
    <div className="h-full flex flex-col items-center justify-center py-4 text-center">
      <div className="flex flex-col items-center justify-center">
        <EmptyMedia variant="default">
          <Icon />
        </EmptyMedia>
        <h2 className="mt-4 text-base font-medium text-foreground">{agentConfig.label}</h2>
        <p className="text-sm text-muted-foreground mt-1">{agentConfig.description}</p>
      </div>
      {agentConfig.starterPrompts.length > 0 && (
        <ul className="mt-6 flex w-full flex-col gap-2">
          {agentConfig.starterPrompts.map((prompt) => {
            const PromptIcon = PROMPT_ICONS[prompt.icon];
            return (
              <li key={prompt.text}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChoosePrompt(prompt)}
                  className={cn(
                    'w-full flex items-baseline gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm text-foreground',
                    'transition-colors duration-100 shadow-xs',
                    'hover:bg-soft hover:border-border-heavy',
                    'active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50'
                  )}
                >
                  <PromptIcon className="size-3 translate-y-0.5 shrink-0 text-muted-foreground/50" />
                  {prompt.text}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
